import * as Api from '@ViewModels';
import type { StyleDeclarationValue } from 'aphrodite';
import { css, StyleSheet } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { useErrorMessages, useFullscreenModal } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import {
	invalidateTelephonyGetConfiguration,
	useTelephonyCampaignResubmitMutation,
	useTelephonyConfigBrandOTPMutation,
	useTelephonyConfigurationForceAllowClientProfileEditMutation,
	useTelephonyConfigurationQuery,
} from '../../../../queries';
import * as AdminViewModels from '../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../web/components/Button';
import { Checkbox } from '../../../../web/components/Checkbox';
import { ConfirmationDialog, DefaultConfirmationOptions } from '../../../../web/components/ConfirmationDialog';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../../web/components/HoverCard';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../../web/components/Modal';
import { CampaignIcon } from '../../../../web/components/svgs/icons/CampaignIcon';
import { RefreshIcon } from '../../../../web/components/svgs/icons/RefreshIcon';
import { StickyNoteIcon } from '../../../../web/components/svgs/icons/StickyNoteIcon';
import { TextingIcon } from '../../../../web/components/svgs/icons/TextingIcon';
import { WarningIcon } from '../../../../web/components/svgs/icons/WarningIcon';
import { brandPrimary, brandPrimaryHover } from '../../../../web/styles/colors';
import { bs } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

const BrandStatusStyles: Partial<Record<Api.TextingBrandStatus, StyleDeclarationValue[]>> = {
	[Api.TextingBrandStatus.Failed]: [bs.textDestructive],
	[Api.TextingBrandStatus.Pending]: [bs.textWarningDark],
	[Api.TextingBrandStatus.Verified]: [bs.textSuccess],
};

const BrandRegistrationStatusIndicator = (props: { impersonationContext: Api.IImpersonationContext }) => {
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('BrandRegistrationStatusIndicator');
	const [showResendOTPConfirmation, setShowResendOTPConfirmation] = React.useState(false);
	const brandOTPMutation = useTelephonyConfigBrandOTPMutation({
		onSuccess: () => {
			toast.success('OTP resent successfully');
		},
		onError: error => {
			logApiError('TelephonyBrandResendOTP', error);
			errorMessages.pushApiError(error);
		},
	});
	const telephonyConfigQuery = useTelephonyConfigurationQuery({
		impersonationContext: props.impersonationContext,
	});
	const brandStatus = telephonyConfigQuery.data?.campaignRegistration?.brand?.status;
	const businessType = telephonyConfigQuery.data?.clientProfile?.businessInformation?.type;
	let popupMessage = '';
	if (telephonyConfigQuery.data?.campaignRegistration?.brand?.errorMessage) {
		popupMessage = telephonyConfigQuery.data.campaignRegistration.brand.errorMessage;
	}
	if (brandStatus === Api.TextingBrandStatus.Pending && businessType === 'SoleProprietorship') {
		popupMessage =
			'On hold pending sole proprietorship support. If the customer is not a sole proprirtor, please reset.';
	}
	const canRetryOTP =
		userSession.hasPermission(AdminViewModels.AdminPermission.SupportLevel3) &&
		telephonyConfigQuery.data?.campaignRegistration?.brand?.status === Api.TextingBrandStatus.Pending &&
		businessType === 'SoleProprietorship';
	const handleResendOTPClick = () => {
		setShowResendOTPConfirmation(true);
	};
	const handleOTPResend = () => {
		brandOTPMutation.mutate({
			impersonationContext: props.impersonationContext,
		});
	};
	return (
		<div className={css(bs.inlineFlex, bs.itemsCenter, bs.textSm, bs.noUnderline, bs.flexWrap)}>
			<TextingIcon className={css(bs.mr2, bs.h4, bs.w4)} fill='#0066a5' />
			Brand Status:&nbsp;
			<strong>
				<span className={css(BrandStatusStyles[brandStatus])}>
					{popupMessage ? (
						<HoverCard>
							<HoverCardTrigger className={css(bs.cursorPointer)}>{brandStatus}</HoverCardTrigger>
							<HoverCardContent styles={[styleSheet.telephonyStatusPopoverContent]}>
								<div className={css(bs.flex, bs.flexCol, bs.gap2, bs.itemsStart)}>
									<div>{popupMessage}</div>
									<div className={css(bs.flex, bs.gap2)}>
										{canRetryOTP ? (
											<button
												className={css(bs.ctaButtonSmall)}
												disabled={brandOTPMutation.isPending}
												onClick={handleResendOTPClick}
											>
												Resend OTP
											</button>
										) : null}
									</div>
								</div>
							</HoverCardContent>
						</HoverCard>
					) : (
						brandStatus
					)}
				</span>
			</strong>
			&nbsp; ({telephonyConfigQuery.data?.clientProfile?.businessInformation?.type ?? 'Unknown Type'})
			<ConfirmationDialog
				icon={<WarningIcon />}
				title='Are you sure you want to resend a brand verification text to the user?'
				modalProps={{
					isOpen: showResendOTPConfirmation,
					onRequestClose: (_, canceled) => {
						if (!canceled) {
							handleOTPResend();
						}
						setShowResendOTPConfirmation(false);
					},
				}}
				options={DefaultConfirmationOptions}
			/>
		</div>
	);
};

export const AccountDetailsBrandRegistrationStatusIndicator = (props: {
	impersonationContext?: Api.IImpersonationContext;
}) => {
	const telephonyConfigQuery = useTelephonyConfigurationQuery({
		impersonationContext: props.impersonationContext,
	});
	if (telephonyConfigQuery.isError) {
		return;
	}
	if (telephonyConfigQuery.isPending) {
		return <LoadingSpinner type='tiny' />;
	}
	if (telephonyConfigQuery.data == null) {
		return (
			<li className={css(bs.flex, bs.itemsCenter, bs.flexWrap, bs.mb2)}>
				<div className={css(bs.inlineFlex, bs.cursorPointer, bs.itemsCenter, bs.textSm, bs.noUnderline, bs.gap2)}>
					<TextingIcon className={css(bs.h4, bs.w4)} fill='#0066a5' />
					<span>No Registration Found</span>
				</div>
			</li>
		);
	}
	return (
		<li className={css(bs.flex, bs.itemsCenter, bs.flexWrap, bs.mb2)}>
			<BrandRegistrationStatusIndicator impersonationContext={props.impersonationContext} />
		</li>
	);
};

const CampaignRegistrationStatusStyles: Partial<Record<Api.CampaignRegistrationStatus, StyleDeclarationValue[]>> = {
	[Api.CampaignRegistrationStatus.Accepted]: [bs.textSuccess],
	[Api.CampaignRegistrationStatus.Deactivated]: [bs.textGray400],
	[Api.CampaignRegistrationStatus.Declined]: [bs.textDestructive],
	[Api.CampaignRegistrationStatus.Failed]: [bs.textDestructive],
	[Api.CampaignRegistrationStatus.Pending]: [bs.textWarningDark],
	[Api.CampaignRegistrationStatus.Verified]: [bs.textSuccess],
};

const CampaignRegistrationStatus = (props: {
	impersonationContext?: Api.IImpersonationContext;
	campaign: Api.TextingProviderCampaign;
}) => {
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('CampaignRegistrationStatus');
	const [resubmitConfirmed, setResubmitConfirmed] = React.useState(false);
	const campaignResubmitMutation = useTelephonyCampaignResubmitMutation({
		onSuccess: () => {
			invalidateTelephonyGetConfiguration();
		},
		onError: error => {
			logApiError('ResubmitTelephonyCampaign', error);
			errorMessages.pushApiError(error);
		},
	});
	const handleResubmit = () => {
		campaignResubmitMutation.mutate({
			impersonationContext: props.impersonationContext,
			campaignId: props.campaign.id,
		});
	};
	const canResubmitCampaign =
		userSession.hasPermission(AdminViewModels.AdminPermission.SupportLevel3) &&
		(props.campaign.status === Api.CampaignRegistrationStatus.Failed ||
			props.campaign.status === Api.CampaignRegistrationStatus.Declined);

	const campaignStatus = (
		<>
			{props.campaign.provider}: &nbsp;
			<span className={css(CampaignRegistrationStatusStyles[props.campaign.status])}>
				<strong>{props.campaign.status}</strong>
			</span>
		</>
	);

	return (
		<span key={props.campaign.id}>
			{props.campaign.errorMessage ? (
				<HoverCard>
					<HoverCardTrigger>{campaignStatus}</HoverCardTrigger>
					<HoverCardContent styles={[styleSheet.telephonyStatusPopoverContent]}>
						<div className={css(CampaignRegistrationStatusStyles[props.campaign.status])}>
							{props.campaign.errorMessage}
						</div>
						{canResubmitCampaign ? (
							<div>
								<br />
								<div>
									Please ensure the customer has addressed the error reason before resubmitting the campaign. If you
									believe this is in error, we can submit an appeal.
								</div>
								<br />
								<Checkbox checked={resubmitConfirmed} onChange={() => setResubmitConfirmed(!resubmitConfirmed)}>
									<span>I confirm issues have been addressed</span>
								</Checkbox>
								<Button
									size='small'
									kind='icon'
									label='Resubmit Campaign'
									disabled={!resubmitConfirmed || campaignResubmitMutation.isPending}
									onClick={handleResubmit}
									accessory={<RefreshIcon width={12} height={12} />}
								/>
							</div>
						) : null}
					</HoverCardContent>
				</HoverCard>
			) : (
				campaignStatus
			)}
		</span>
	);
};

export const AccountDetailsCampaignRegistrationStatusList = (props: {
	impersonationContext: Api.IImpersonationContext;
}) => {
	const telephonyConfigQuery = useTelephonyConfigurationQuery({
		impersonationContext: props.impersonationContext,
	});

	const [selectedCampaign, setSelectedCampaign] = React.useState(null);

	// Filter campaigns to show all accepted and pending, or most recent if none
	const filteredCampaigns = React.useMemo(() => {
		const campaigns = telephonyConfigQuery.data?.campaignRegistration?.campaigns || [];
		const campaignsByProvider = new Map<Api.TelephonyServiceProvider, Api.TextingProviderCampaign[]>();

		// Group campaigns by provider
		campaigns.forEach(campaign => {
			if (!campaignsByProvider.has(campaign.provider)) {
				campaignsByProvider.set(campaign.provider, []);
			}
			campaignsByProvider.get(campaign.provider)?.push(campaign);
		});

		// For each provider, show all accepted and pending campaigns, or most recent if none
		const result: Api.TextingProviderCampaign[] = [];
		campaignsByProvider.forEach(providerCampaigns => {
			const acceptedAndPending = providerCampaigns.filter(
				campaign =>
					campaign.status === Api.CampaignRegistrationStatus.Accepted ||
					campaign.status === Api.CampaignRegistrationStatus.Pending
			);

			if (acceptedAndPending.length > 0) {
				result.push(...acceptedAndPending);
			} else if (providerCampaigns.length > 0) {
				// Find most recent campaign
				const mostRecent = providerCampaigns.reduce((latest, current) => {
					const latestTime = latest.lastSubmissionDate ? new Date(latest.lastSubmissionDate).getTime() : 0;
					const currentTime = current.lastSubmissionDate ? new Date(current.lastSubmissionDate).getTime() : 0;
					return currentTime > latestTime ? current : latest;
				});
				result.push(mostRecent);
			}
		});

		return result;
	}, [telephonyConfigQuery.data?.campaignRegistration?.campaigns]);

	if (telephonyConfigQuery.isError) {
		return;
	}
	if (telephonyConfigQuery.isPending) {
		return <LoadingSpinner type='tiny' />;
	}
	const hasNoCampaigns = filteredCampaigns.length === 0;
	if (hasNoCampaigns) {
		return null;
	}

	const handleCampaignClick = (campaign: Api.TextingProviderCampaign) => {
		setSelectedCampaign(campaign);
	};
	const closeCampaignDetailModal = () => {
		setSelectedCampaign(null);
	};

	return (
		<li className={css(bs.flex, bs.itemsCenter, bs.flexWrap, bs.mb2, bs.textSm)}>
			<div className={css(bs.inlineFlex, bs.cursorPointer, bs.itemsCenter, bs.noUnderline, bs.gap2)}>
				<CampaignIcon className={css(bs.h4, bs.w4)} />
				<span>Campaign Status</span>
			</div>
			<ul className={css(bs.ml7, bs.mt1, bs.wFull)}>
				{filteredCampaigns.map(campaign => {
					return (
						<li
							key={`campaign-${campaign.id}`}
							onClick={() => handleCampaignClick(campaign)}
							style={{ cursor: 'pointer' }}
						>
							<CampaignRegistrationStatus impersonationContext={props.impersonationContext} campaign={campaign} />
						</li>
					);
				})}
			</ul>
			{selectedCampaign && (
				<CampaignDetailsModal
					campaign={selectedCampaign}
					modalProps={{ isOpen: selectedCampaign != null, onRequestClose: closeCampaignDetailModal }}
				/>
			)}
		</li>
	);
};

export const AccountDetailsAllowRegistrationEdit = (props: { impersonationContext: Api.IImpersonationContext }) => {
	const telephonyConfigQuery = useTelephonyConfigurationQuery({
		impersonationContext: props.impersonationContext,
	});
	const userSession = useAdminUserSession();
	const [showAllowEditConfirmation, setShowAllowEditConfirmation] = React.useState(false);
	const reset = useTelephonyConfigurationForceAllowClientProfileEditMutation();
	const handleAllowEditClick = () => {
		setShowAllowEditConfirmation(true);
	};
	if (telephonyConfigQuery.isError) {
		return;
	}
	if (telephonyConfigQuery.isPending) {
		return <LoadingSpinner type='tiny' />;
	}

	const hasUnverifiedCampaign = telephonyConfigQuery.data?.campaignRegistration?.campaigns?.some(
		x =>
			x.status === Api.CampaignRegistrationStatus.Pending ||
			x.status === Api.CampaignRegistrationStatus.Failed ||
			x.status === Api.CampaignRegistrationStatus.Declined
	);

	const hasNoAcceptedCampaigns = telephonyConfigQuery.data?.campaignRegistration?.campaigns?.every(
		x => x.status !== Api.CampaignRegistrationStatus.Accepted
	);

	const hasNoCampaigns = telephonyConfigQuery.data?.campaignRegistration?.campaigns?.length === 0;

	const canAllowForceClientProfileEdit =
		userSession.hasPermission(AdminViewModels.AdminPermission.SupportLevel3) &&
		!telephonyConfigQuery.data?.forceAllowClientProfileEdit;

	if ((hasUnverifiedCampaign || hasNoCampaigns) && hasNoAcceptedCampaigns && canAllowForceClientProfileEdit) {
		return (
			<li className={css(bs.flex, bs.itemsCenter, bs.flexWrap, bs.mb2, bs.textSm)}>
				<HoverCard>
					<HoverCardTrigger className={css(bs.cursorPointer)}>
						<CampaignIcon className={css(bs.h4, bs.w4)} /> Allow registration edits
					</HoverCardTrigger>
					<HoverCardContent styles={[styleSheet.telephonyStatusPopoverContent]}>
						<div className={css(bs.flex, bs.flexCol, bs.gap2, bs.itemsStart)}>
							<div>
								Allow customers to make texting campaign changes. Once complete, customers can go directly to the
								registration form:
							</div>
							<div>
								<a
									href='https://secure.levitate.ai/#/texting/registration/form'
									target='_blank'
									rel='noreferrer'
									className={css(bs.textBrandPrimary, bs.fontBold)}
								>
									https://secure.levitate.ai/#/texting/registration/form
								</a>
							</div>
							<div className={css(bs.flex, bs.gap2)}>
								<button className={css(bs.ctaButtonSmall)} disabled={reset.isPending} onClick={handleAllowEditClick}>
									Allow registration edits
								</button>
							</div>
						</div>
					</HoverCardContent>
				</HoverCard>
				<ConfirmationDialog
					icon={<WarningIcon />}
					title='Allow editing existing texting registration'
					modalProps={{
						isOpen: showAllowEditConfirmation,
						onRequestClose: (_, canceled) => {
							if (!canceled) {
								reset.mutate({
									impersonationContext: props.impersonationContext,
								});
							}
							setShowAllowEditConfirmation(false);
						},
					}}
					options={DefaultConfirmationOptions}
				>
					<p>
						Please send this link to the customer:{' '}
						<a
							href='https://secure.levitate.ai/#/texting/registration/form'
							target='_blank'
							rel='noreferrer'
							className={css(bs.textBrandPrimary, bs.fontBold)}
						>
							https://secure.levitate.ai/#/texting/registration/form
						</a>
					</p>
				</ConfirmationDialog>
			</li>
		);
	}
	return null;
};

const CampaignDetails: React.FC<{ campaign: Api.TextingProviderCampaign }> = ({ campaign }) => (
	<div>
		<h2>Text Campaign Details</h2>
		<div>
			<strong>Id:</strong> <span>{campaign.id}</span>
		</div>
		<div>
			<strong>Status:</strong> <span>{campaign.status}</span>
		</div>
		<div>
			<strong>Provider:</strong> <span>{campaign.provider}</span>
		</div>
		<div>
			<strong>Last Submission Date:</strong>{' '}
			<span>{campaign.lastSubmissionDate ? moment(campaign.lastSubmissionDate).format('MM/DD/YY h:mmA') : 'N/A'}</span>
		</div>
		{campaign.errorMessage && (
			<div>
				<strong>Error:</strong> <span>{campaign.errorMessage}</span>
			</div>
		)}
	</div>
);
const CampaignDetailsModal = asModalComponent(CampaignDetails);

export const CopyMageButton = (props: { accountId: string }) => {
	const fullscreenModal = useFullscreenModal();
	const handleLinkClick = (ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
		ev.preventDefault();
		fullscreenModal.history.push(`/copymage/${props.accountId}/latest-conversation`);
	};
	return (
		<a href='#' className={css(s.copyMageLink)} onClick={handleLinkClick}>
			<StickyNoteIcon className={css(styleSheet.iconStickyNote)} />
			<span>Website Copy Mage</span>
		</a>
	);
};

const s = StyleSheet.create({
	copyMageLink: {
		':hover': {
			color: brandPrimaryHover,
			textDecoration: 'underline',
		},
		alignItems: 'center',
		color: brandPrimary,
		cursor: 'pointer',
		display: 'inline-flex',
		fontSize: 14,
		textDecoration: 'none',
		gap: 4,
	},
});
